import { Injectable } from '@angular/core';
import { TramApiService } from '../tram-api.service';
import { PointOfInterest, POIType } from '../core/poi';
import { LiteEvent } from './index';

@Injectable()
export class PointOfInterestCollector {
    private _points: Map<number, PointOfInterest>;
    get points(): PointOfInterest[] {
        let points: PointOfInterest[] = [];
        this._points.forEach(p => {
            points.push(p);
        });

        return points;
    }

    private readonly _onBeforeSelect = new LiteEvent<PointOfInterest>();
    private readonly _onAfterSelect = new LiteEvent<PointOfInterest>();
    private readonly _onAfterEnable = new LiteEvent<any>();
    private readonly _onAfterDisable = new LiteEvent<any>();

    onPOIInitialized: (point: PointOfInterest) => void;
    get beforeSelect() { return this._onBeforeSelect.expose(); }
    get afterSelect() { return this._onAfterSelect.expose(); }
    get afterEnable() { return this._onAfterEnable.expose(); }
    get afterDisable() { return this._onAfterDisable.expose(); }

    constructor(private api: TramApiService) {
        this._points = new Map<number, PointOfInterest>();
    }

    init() {
        this.api.getMalls().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getSupermarkets().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getUniversities().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getSchools().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getKindergartens().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getHospitals().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getPolice().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getStations().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
        this.api.getEntertainment().then(points => {
            points.forEach(p => {
                this.prepareInternal(p);
                this._points.set(p.id, p);

                if (this.onPOIInitialized)
                    this.onPOIInitialized(p);
            });
        });
    }

    select(point: PointOfInterest): void;
    select(type: POIType): void;
    select(obj: any) {
        if (obj instanceof PointOfInterest) {
            this.selectPoint(obj);
        } else {
            let type = obj;
            this._points.forEach(p => {
                if (p.type === type)
                    p.select();
            });
        }
    }

    unselect() {
        this._points.forEach(p => {
            if (p.isSelected) {
                p.unselect();
            }
        });
    }

    enable(type: POIType) {
        new Promise<any>(resolve => {
            let enabledPoints: PointOfInterest[] = [];
            this._points.forEach(p => {
                if (type === POIType.Mall) {
                    switch (p.type) {
                        case POIType.Mall:
                        case POIType.Supermarket:
                            p.enable();

                            enabledPoints.push(p);
                            break;
                    }
                } else if (type === POIType.Hospital) {
                    switch (p.type) {
                        case POIType.Kindergarten:
                        case POIType.School:
                        case POIType.University:
                        case POIType.Hospital:
                        case POIType.Clinic:
                        case POIType.Doctor:
                        case POIType.Police:
                            p.enable();

                            enabledPoints.push(p);
                            break;
                    }
                } else if (type === POIType.SubwayStation) {
                    switch (p.type) {
                        case POIType.SubwayStation:
                        case POIType.RailwayStation:
                            p.enable();

                            enabledPoints.push(p);
                            break;
                    }
                } else if (type === POIType.Cafe) {
                    switch (p.type) {
                        case POIType.Cafe:
                        case POIType.Cinema:
                        case POIType.Theatre:
                        case POIType.Pub:
                        case POIType.Nightclub:
                        case POIType.Restaurant:
                        case POIType.Bar:
                            p.enable();

                            enabledPoints.push(p);
                            break;
                    }
                }
            });

            if (this._onAfterEnable)
                this._onAfterEnable.trigger({ type, enabledPoints });
        });
    }

    disable(type: POIType) {
        new Promise<any>(resolve => {
            let disabledPoints: PointOfInterest[] = [];
            this._points.forEach(p => {
                if (type === POIType.Mall) {
                    switch (p.type) {
                        case POIType.Mall:
                        case POIType.Supermarket:
                            if (p.isSelected)
                                p.unselect();
                            p.disable();

                            disabledPoints.push(p);
                            break;
                    }
                } else if (type === POIType.Hospital) {
                    switch (p.type) {
                        case POIType.Kindergarten:
                        case POIType.School:
                        case POIType.University:
                        case POIType.Hospital:
                        case POIType.Clinic:
                        case POIType.Doctor:
                        case POIType.Police:
                            if (p.isSelected)
                                p.unselect();
                            p.disable();

                            disabledPoints.push(p);
                            break;
                    }
                } else if (type === POIType.SubwayStation) {
                    switch (p.type) {
                        case POIType.SubwayStation:
                        case POIType.RailwayStation:
                            if (p.isSelected)
                                p.unselect();
                            p.disable();

                            disabledPoints.push(p);
                            break;
                    }
                } else if (type === POIType.Cafe) {
                    switch (p.type) {
                        case POIType.Cafe:
                        case POIType.Cinema:
                        case POIType.Theatre:
                        case POIType.Pub:
                        case POIType.Nightclub:
                        case POIType.Restaurant:
                        case POIType.Bar:
                            if (p.isSelected)
                                p.unselect();
                            p.disable();

                            disabledPoints.push(p);
                            break;
                    }
                }
            });

            if (this._onAfterDisable)
                this._onAfterDisable.trigger({ type, disabledPoints });
        });


    }

    private prepareInternal(point: PointOfInterest) {
        point.init();
        point.marker.on('click', e => this.selectInternal(e, point));
    }

    private selectPoint(point: PointOfInterest) {
        if (!this._points.has(point.id))
            return;

        let pointInternal = this._points.get(point.id);
        this.selectInternal(null, pointInternal);
    }

    private selectInternal(e: any, point: PointOfInterest) {
        if (this._onBeforeSelect)
            this._onBeforeSelect.trigger(point);

        // unselect all selected poi before
        this._points.forEach(p => {
            if (!p.isSelected)
                return;

            p.unselect();
        });

        point.select();

        if (this._onAfterSelect)
            this._onAfterSelect.trigger(point);
    }
}


// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/poi-collector.ts